/**
 * Where a file sits, in words, for views that span folders (All files,
 * Search). Pure — no React.
 *
 * Unlike the file dialogs' `describeFolderPath`, which never shows My-Fias,
 * the workspace shows every area, and it names folders by their display
 * names — an arche's folder under `/My-Fias/Arches/<archeId>` is named after
 * the arche, while its path holds the raw id.
 */
import type { VaultDocumentFolderDto } from '@/lib/api';
import { vaultAreaOfPath, type VaultArea } from '@/lib/vault-areas';

export const AREA_NAMES: Record<VaultArea, string> = {
  documents: 'Documents',
  'arche-saves': 'Arche Saves',
  'my-fias': 'My-Fias',
};

/** "Documents / Projects", "Arche Saves / AI Images", "My-Fias / Arches /
 *  Image Editor". `null` folder = the Holder root = Documents. An unknown
 *  folder (not loaded yet, or trashed) falls back to its area's name. */
export function describeFolderLocation(
  folderId: string | null,
  folders: readonly VaultDocumentFolderDto[],
): string {
  if (!folderId) return AREA_NAMES.documents;
  const byId = new Map(folders.map((f) => [f.folderId, f]));
  const start = byId.get(folderId);
  if (!start) return AREA_NAMES.documents;
  const area = vaultAreaOfPath(start.path);
  const names: string[] = [];
  let current: VaultDocumentFolderDto | undefined = start;
  // Bounded walk: a malformed parent loop must not hang a render.
  for (let guard = 0; current && guard < 64; guard += 1) {
    names.unshift(current.name);
    current = current.parentFolderId ? byId.get(current.parentFolderId) : undefined;
  }
  // The system roots ARE the area; don't name them twice.
  if (area !== 'documents') names.shift();
  return [AREA_NAMES[area], ...names].join(' / ');
}
